import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadCloud, X, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { fileName } from "@/lib/attachments";
import { AttachmentThumb } from "@/components/AttachmentThumb";

interface Props {
  label: string;
  folder: string;
  urls: string[];
  onChange: (urls: string[]) => void;
  accept?: string;
  addLabel?: string;
  /** Also let people paste a link (a shared drive file, a web page) next to the upload. */
  allowLink?: boolean;
  linkPlaceholder?: string;
  linkButtonLabel?: string;
}

/** Real file-attachment uploader for a request's supporting documents. Uploads each
 *  selected file to Cloudinary (via /upload) and appends the URL to the list. */
export function AttachmentField({
  label,
  folder,
  urls,
  onChange,
  accept = ".pdf,.jpg,.jpeg,.png,.heic,.webp",
  addLabel,
  allowLink,
  linkPlaceholder = "https://...",
  linkButtonLabel,
}: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState("");
  const linkOk = /^https?:\/\/\S+$/i.test(link.trim());
  function addLink() {
    if (!linkOk) return;
    onChange([...urls, link.trim()]);
    setLink("");
  }

  async function handleFiles(files: FileList) {
    setError(null);
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post(`/upload?folder=${folder}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      onChange([...urls, ...uploaded]);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? t("common.error"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div>
      <label className="label">{label}</label>
      {urls.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {urls.map((u, i) => (
            <li key={i} className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm">
              <a href={u} target="_blank" rel="noreferrer" className="shrink-0" title={fileName(u)}>
                <AttachmentThumb url={u} size={96} className="h-12 w-12" />
              </a>
              <a href={u} target="_blank" rel="noreferrer" className="flex-1 truncate text-primary hover:underline">
                {fileName(u)}
              </a>
              <button
                type="button"
                className="text-slate-400 hover:text-red-600"
                onClick={() => onChange(urls.filter((_, j) => j !== i))}
                aria-label={t("common.delete")}
              >
                <X size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) handleFiles(e.target.files);
        }}
      />
      <button
        type="button"
        className="btn-secondary text-sm"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? t("legal.uploading") : (
          <>
            <UploadCloud size={14} /> {addLabel ?? t("worksPermits.addAttachment")}
          </>
        )}
      </button>
      {allowLink && (
        <div className="mt-2 flex gap-2">
          <input
            type="url"
            className="input text-sm"
            placeholder={linkPlaceholder}
            value={link}
            onChange={(e) => setLink(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addLink();
              }
            }}
          />
          <button type="button" className="btn-secondary text-sm shrink-0" disabled={!linkOk} onClick={addLink}>
            <Link2 size={14} /> {linkButtonLabel ?? t("meetings.addLink")}
          </button>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
